import { hasSupabaseConfig, supabaseRequest } from './supabaseClient';
import { fallbackData } from './fallbackData';
const SITE_DATA_KEY = 'glc_site_data';

const getSiteDataSection = async (section, fallbackKey = section) => {
  const result = await getSiteData();
  if (result?.data?.[section]) return result.data[section];
  return fallbackData[fallbackKey] ?? [];
};

export const getServices = () =>
  getSiteDataSection('services');

export const getTestimonials = () =>
  getSiteDataSection('testimonials');

export const getTeam = () =>
  getSiteDataSection('team');

export const getProjects = () =>
  getSiteDataSection('projects');

export const getProducts = () =>
  getSiteDataSection('products');

export const getFaq = () =>
  getSiteDataSection('faq');

export const getBlog = async (searchParams = {}) => {
  const { category, sort = 'desc' } = searchParams;
  const blog = await getSiteDataSection('blog');
  return [...blog]
    .filter((post) => !category || post.category === category)
    .sort((a, b) => {
      const left = new Date(a.published_at || a.created_at || 0).getTime();
      const right = new Date(b.published_at || b.created_at || 0).getTime();
      return sort === 'asc' ? left - right : right - left;
    });
};

export const submitContact = async (payload) => {
  if (!hasSupabaseConfig) {
    console.warn('Supabase not configured. Contact payload stored locally only.');
    return { error: null };
  }

  const response = await supabaseRequest('contact_messages', {
    method: 'POST',
    headers: { Prefer: 'return=minimal' },
    body: JSON.stringify(payload),
  });
  return { error: response.ok ? null : { message: await response.text() } };
};

export const getSiteData = async () => {
  if (!hasSupabaseConfig) {
    return { data: fallbackData, error: null, source: 'fallback' };
  }

  // Prefer public schema table for shared DB setups.
  const publicRes = await supabaseRequest(`glc_site_data?select=data&key=eq.${SITE_DATA_KEY}`);
  if (publicRes.ok) {
    const rows = await publicRes.json();
    return { data: rows?.[0]?.data || fallbackData, error: null, source: 'supabase:public' };
  }

  if (publicRes.status === 401 || publicRes.status === 403) {
    const error = { message: await publicRes.text() };
    console.warn('Supabase unauthorized for site_data (public):', error.message);
    return { data: fallbackData, error, source: 'fallback' };
  }

  // Fallback to legacy custom schema table.
  const glcRes = await supabaseRequest(`site_data?select=data&key=eq.${SITE_DATA_KEY}`, { profile: 'glc' });
  if (glcRes.ok) {
    const rows = await glcRes.json();
    return { data: rows?.[0]?.data || fallbackData, error: null, source: 'supabase:glc' };
  }

  const error = { message: await glcRes.text() };
  console.warn('Supabase error for site_data:', error.message);
  return { data: fallbackData, error, source: 'fallback' };
};

const shouldBypassServerSave = () => {
  if (typeof window === 'undefined') return false;
  const host = String(window.location.hostname || '').toLowerCase();
  const isLocalHost = host === 'localhost' || host === '127.0.0.1';
  return isLocalHost && window.location.port !== '8888';
};

const saveSiteDataDirect = async (payload) => {
  if (!hasSupabaseConfig) {
    return {
      error: { message: 'Supabase not configured' },
      status: null,
    };
  }

  const requestOptions = {
    method: 'PATCH',
    headers: { Prefer: 'return=minimal' },
    body: JSON.stringify({ data: payload }),
  };
  const publicRes = await supabaseRequest(`glc_site_data?key=eq.${SITE_DATA_KEY}`, requestOptions);

  if (publicRes.ok) {
    return { error: null, source: 'supabase:public:client' };
  }

  const glcRes = await supabaseRequest(`site_data?key=eq.${SITE_DATA_KEY}`, {
    ...requestOptions,
    profile: 'glc',
  });

  if (glcRes.ok) {
    return { error: null, source: 'supabase:glc:client' };
  }

  const publicMessage = await publicRes.text();
  const glcMessage = await glcRes.text();
  return {
    error: {
      message: glcMessage || publicMessage || 'Failed to save site data directly via Supabase',
    },
    status: glcRes.status || publicRes.status || null,
  };
};

export const saveSiteData = async (payload) => {
  if (shouldBypassServerSave()) {
    return saveSiteDataDirect(payload);
  }

  try {
    const response = await fetch('/.netlify/functions/site-data-save', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ data: payload }),
    });

    const body = await response.json().catch(() => ({}));
    if (!response.ok || body?.ok === false) {
      if (response.status === 404) {
        return saveSiteDataDirect(payload);
      }

      return {
        error: { message: body?.error || 'Failed to save site data via server function' },
        status: response.status || null,
      };
    }

    return { error: null, source: body?.source || 'netlify:function' };
  } catch (error) {
    return saveSiteDataDirect(payload).catch(() => ({
      error: { message: error?.message || 'Unexpected site-data save error' },
      status: null,
    }));
  }
};
